import React, { useState, useEffect } from 'react';
import { UserProfile, RecentUpload } from '../types';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { supabaseService } from '../services/supabaseService';
import LoadingSpinner from './LoadingSpinner';
import ScanDetailCard from './ScanDetailCard';

interface UserProfileModalProps {
  user: UserProfile;
  onClose: () => void;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({ user, onClose }) => {
  const [uploads, setUploads] = useState<RecentUpload[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedScan, setSelectedScan] = useState<RecentUpload | null>(null);

  useEffect(() => {
    const fetchUploads = async () => {
      if (!user) return;
      setLoading(true);
      try {
        const uploadsData = await supabaseService.getUserScans(user.id);
        setUploads(uploadsData.map(scan => ({
          ...scan,
          uploader_name: user.name, // Ensure uploader_name is populated from user prop
        })));
      } catch (error) {
        console.error('Error fetching user uploads:', error);
        setUploads([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUploads();
  }, [user]);

  if (!user) return null;

  const handleScanClick = (scan: RecentUpload) => {
    setSelectedScan(selectedScan?.id === scan.id ? null : scan);
  };

  const getStatusClass = (status: string) => {
    if (status === 'completed') return 'bg-green-500 text-white';
    if (status === 'processing') return 'bg-yellow-500 text-black';
    return 'bg-red-500 text-white';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex justify-center items-center p-4 transition-opacity duration-300">
      <div className="bg-gray-900 text-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] flex flex-col animate-fade-in-up">
        <div className="p-5 border-b border-gray-700 flex justify-between items-center">
          <h2 className="text-xl font-bold tracking-wider">
            User Profile: <span className="text-indigo-400">{user.name}</span>
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <XMarkIcon className="h-7 w-7" />
          </button>
        </div>
        <div className="p-6 flex-grow overflow-y-auto space-y-8">
          <div>
            <h3 className="text-lg font-semibold text-indigo-300 mb-4">Account Information</h3>
            <div className="bg-gray-800 p-6 rounded-lg grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <p><strong className="font-medium text-gray-400">Email:</strong> {user.email}</p>
                <p><strong className="font-medium text-gray-400">Joined:</strong> {new Date(user.created_at).toLocaleDateString()}</p>
              </div>
              <div className="space-y-3">
                <p><strong className="font-medium text-gray-400">Role:</strong> <span className="capitalize px-2 py-1 bg-indigo-500 text-white rounded-full text-xs">{user.role}</span></p>
                <p><strong className="font-medium text-gray-400">Last Login:</strong> {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}</p>
                <p><strong className="font-medium text-gray-400">Subscription Ends:</strong> {user.subscription_end_date ? new Date(user.subscription_end_date).toLocaleDateString() : 'N/A'}</p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-indigo-300 mb-4">Upload History</h3>
            {loading ? (
              <div className="flex justify-center items-center h-48">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="space-y-4">
                {uploads.length > 0 ? (
                  uploads.map((upload) => (
                    <React.Fragment key={upload.id}>
                      <div
                        className="bg-gray-800 p-4 rounded-lg cursor-pointer border border-transparent hover:border-indigo-500 transition-all duration-300"
                        onClick={() => handleScanClick(upload)}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-semibold text-white">{upload.title}</p>
                            <p className="text-sm text-gray-400">{new Date(upload.upload_time).toLocaleString()}</p>
                          </div>
                          <span className={`capitalize px-3 py-1 text-xs rounded-full ${getStatusClass(upload.status)}`}>
                            {upload.status}
                          </span>
                        </div>
                      </div>
                      {selectedScan?.id === upload.id && (
                        <div className="p-4 bg-gray-800 rounded-lg mt-2 animate-fade-in">
                          <ScanDetailCard scan={selectedScan} />
                        </div>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <p className="text-center text-gray-500 py-8">This user has not uploaded any scans.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileModal;